import React, { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import toast from "react-hot-toast";
import axios from "axios";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getToken = () => sessionStorage.getItem("access_token");
const getTenantId = () => JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id;
const getSocketUrl = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000";
  return baseUrl.replace(/\/api\/?$/, "");
};

const formatTime = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ─── Status indicator ─────────────────────────────────────────────────────────
const StatusDot = ({ status }) => {
  const colors = {
    connected: "#22c55e",
    connecting: "#f59e0b",
    disconnected: "#ef4444",
  };
  const labels = { connected: "Live", connecting: "Connecting…", disconnected: "Offline" };
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: colors[status] }}>
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: colors[status],
          display: "inline-block",
          boxShadow: status === "connected" ? `0 0 6px ${colors[status]}` : "none",
          animation: status === "connecting" ? "pulse 1.2s infinite" : "none",
        }}
      />
      {labels[status]}
    </span>
  );
};

// ─── Message bubble ───────────────────────────────────────────────────────────
const MessageBubble = ({ msg, isAdmin }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: isAdmin ? "flex-end" : "flex-start",
      marginBottom: 8,
    }}
  >
    <div
      style={{
        maxWidth: "72%",
        padding: "9px 14px",
        borderRadius: isAdmin ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
        background: isAdmin
          ? "linear-gradient(135deg, #3b82f6, #2563eb)"
          : "#f1f5f9",
        color: isAdmin ? "#fff" : "#1e293b",
        fontSize: 14,
        lineHeight: 1.5,
        boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
        wordBreak: "break-word",
      }}
    >
      {/* Image attachment */}
      {msg.attachment_url && msg.attachment_type === "image" && (
        <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginBottom: 6 }}>
          <img
            src={msg.attachment_url}
            alt={msg.attachment_name || "Image"}
            style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 12, objectFit: "cover" }}
          />
        </a>
      )}
      {/* PDF attachment */}
      {msg.attachment_url && msg.attachment_type === "pdf" && (
        <a
          href={msg.attachment_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 12px",
            borderRadius: 10,
            marginBottom: 6,
            background: isAdmin ? "rgba(255, 0, 0, 0.15)" : "#f8fafc",
            border: isAdmin ? "none" : "1px solid #e2e8f0",
            textDecoration: "none",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={isAdmin ? "#93c5fd" : "#ef4444"} strokeWidth="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: isAdmin ? "#fff" : "#1e293b" }}>
              {msg.attachment_name || "Document.pdf"}
            </div>
            <div style={{ fontSize: 10, color: isAdmin ? "#93c5fd" : "#94a3b8" }}>PDF · Click to open</div>
          </div>
        </a>
      )}
      {msg.message && msg.message}
    </div>
    <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
      {!isAdmin && <span style={{ fontWeight: 600, color: "#64748b" }}>{msg.sender_name}</span>}
      <span>{formatTime(msg.created_at)}</span>
      {isAdmin && (
        <span title={msg.is_read ? "Read" : "Delivered"}>
          {msg.is_read ? (
            <svg width="14" height="10" viewBox="0 0 16 10" fill="none"><path d="M1 5L5 9L15 1" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round"/><path d="M5 5L9 9" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round"/></svg>
          ) : (
            <svg width="14" height="10" viewBox="0 0 16 10" fill="none"><path d="M1 5L5 9L15 1" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"/></svg>
          )}
        </span>
      )}
    </div>
  </div>
);

// ─── Date divider ─────────────────────────────────────────────────────────────
const DateDivider = ({ label }) => (
  <div style={{ textAlign: "center", margin: "12px 0" }}>
    <span
      style={{
        background: "#e2e8f0",
        color: "#64748b",
        fontSize: 11,
        padding: "2px 10px",
        borderRadius: 12,
      }}
    >
      {label}
    </span>
  </div>
);

// ─── Main SupportChat component ───────────────────────────────────────────────
const SupportChat = ({ queryId, queryInfo, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputMsg, setInputMsg] = useState("");
  const [socketStatus, setSocketStatus] = useState("connecting");
  const [sending, setSending] = useState(false);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const slug = getTenantId();

  // Local query status tracking
  const [currentQueryStatus, setCurrentQueryStatus] = useState(queryInfo?.status);
  const [currentClosingReason, setCurrentClosingReason] = useState(queryInfo?.closing_reason);

  useEffect(() => {
    setCurrentQueryStatus(queryInfo?.status);
    setCurrentClosingReason(queryInfo?.closing_reason);
  }, [queryInfo]);

  // Attachment state
  const [attachment, setAttachment] = useState(null); // { file, preview, type }
  const [uploading, setUploading] = useState(false);

  // ── Scroll to bottom ────────────────────────────────────────────────────────
  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // ── Socket connection ────────────────────────────────────────────────────────
  useEffect(() => {
    const socket = io(`${getSocketUrl()}/support-chat`, {
      auth: { token: getToken(), slug },
      transports: ["polling", "websocket"], // ✅ Starts with polling, upgrades to websocket
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });


    socketRef.current = socket;

    socket.on("connect", () => {
      setSocketStatus("connected");
      socket.emit("chat:join", { support_query_id: queryId, slug });
      socket.emit("chat:mark_read", { support_query_id: queryId, slug });
    });

    socket.on("connect_error", () => setSocketStatus("disconnected"));
    socket.on("disconnect", () => setSocketStatus("disconnected"));
    socket.on("reconnecting", () => setSocketStatus("connecting"));

    socket.on("chat:history", ({ messages: hist }) => {
      setMessages(hist || []);
    });

    socket.on("chat:new_message", (msg) => {
      setMessages((prev) => {
        // Avoid duplicates
        if (prev.find((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      // Mark as read immediately since window is open
      socket.emit("chat:mark_read", { support_query_id: queryId, slug });

      // Play sound + browser notification for messages from app users
      if (msg.sender_type === "app_user") {
        // Sound alert
        try {
          const beep = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACA" +
            "f39/f4B/f3+Af39/gH9/f4B/f39/gH9/gH9/f4B/f3+Af39/f4B/f4B/f39/gIB/f39/gICA" +
            "f4CAgIB/gICAgICAgICBgYGBgoKBgoKCgoODg4OEhISEhYWFhYaGhoeHh4eIiIiJiYmKioqL" +
            "i4uMjIyNjY2Ojo6Pj4+QkJCRkZGSkpKTk5OUlJSVlZWWlpaXl5eYmJiZmZmampqbm5ucnJyd" +
            "nZ2enp6fn5+goKChoaGioqKjo6OkpKSlpaWmpqanp6eoqKipqamqqqqrq6usrKytra2urq6vr6+w" +
            "sLCxsbGysrKzs7O0tLS1tbW2tra3t7e4uLi5ubm6urq7u7u8vLy9vb2+vr6/v7/AwMDBwcHCwsLD" +
            "w8PExMTFxcXGxsbHx8fIyMjJycnKysrLy8vMzMzNzc3Ozs7Pz8/Q0NDR0dHS0tLT09PU1NTV1dXW" +
            "1tbX19fY2NjZ2dna2trb29vc3Nzd3d3e3t7f39/g4ODh4eHi4uLj4+Pk5OTl5eXm5ubn5+fo6Ojp" +
            "6enq6urr6+vs7Ozt7e3u7u7v7+/w8PDx8fHy8vLz8/P09PT19fX29vb39/f4+Pj5+fn6+vr7+/v8" +
            "/Pz9/f3+/v7//wAA");
          beep.volume = 0.3;
          beep.play().catch(() => {});
        } catch (e) {}

        // Browser notification when tab is not focused
        if (document.hidden && "Notification" in window && Notification.permission === "granted") {
          let body = msg.message || "";
          if (msg.attachment_type === "image") body = "\ud83d\udcf7 Image" + (msg.message ? `: ${msg.message}` : "");
          if (msg.attachment_type === "pdf") body = "\ud83d\udcc4 Document" + (msg.message ? `: ${msg.message}` : "");
          const notif = new Notification(`${msg.sender_name}`, {
            body,
            tag: `chat-msg-${queryId}`,
          });
          setTimeout(() => notif.close(), 5000);
        }
      }
    });

    socket.on("chat:read_ack", ({ support_query_id }) => {
      if (support_query_id === queryId) {
        setMessages((prev) =>
          prev.map((m) => ({ ...m, is_read: true }))
        );
      }
    });

    socket.on("chat:ended", ({ status, closing_reason }) => {
      setCurrentQueryStatus(status);
      setCurrentClosingReason(closing_reason);
    });

    socket.on("chat:status_changed", ({ status }) => {
      setCurrentQueryStatus(status);
    });

    socket.on("chat:error", ({ message }) => {
      toast.error(message || "Chat error occurred");
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryId, slug]);

  const handleEndConversation = () => {
    const reason = window.prompt("Enter a closing reason for this ticket:");
    if (reason === null) return; // User cancelled
    if (!reason.trim()) {
      toast.error("Closing reason is required.");
      return;
    }
    socketRef.current?.emit("chat:end_conversation", {
      support_query_id: queryId,
      slug,
      closing_reason: reason.trim(),
    });
  };

  // ── File picker ──────────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";
    if (!isImage && !isPdf) {
      toast.error("Only images and PDFs are allowed.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File size must be under 15 MB.");
      return;
    }

    const preview = isImage ? URL.createObjectURL(file) : null;
    setAttachment({ file, preview, type: isImage ? "image" : "pdf" });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const clearAttachment = () => {
    if (attachment?.preview) URL.revokeObjectURL(attachment.preview);
    setAttachment(null);
  };

  // ── Upload attachment to S3 ─────────────────────────────────────────────────
  const uploadAttachment = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";
    const res = await axios.post(`${baseUrl}/tenant/support-query-chat/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        "Authorization": `Bearer ${getToken()}`,
        "slug": slug,
      },
    });
    console.log(">>>>>>>>>>>>>" , res?.data)
    return res.data?.body; // { attachment_url, attachment_type, attachment_name }
  };

  // ── Send message ─────────────────────────────────────────────────────────────
  const handleSend = useCallback(async () => {
    const msg = inputMsg.trim();
    if ((!msg && !attachment) || socketStatus !== "connected") return;

    let attachData = null;

    if (attachment) {
      try {
        setUploading(true);
        setSending(true);
        attachData = await uploadAttachment(attachment.file);
      } catch (err) {
        console.error("Upload failed:", err);
        toast.error("Failed to upload file. Please try again.");
        setUploading(false);
        setSending(false);
        return;
      } finally {
        setUploading(false);
      }
    } else {
      setSending(true);
    }

    socketRef.current?.emit("chat:send_message", {
      support_query_id: queryId,
      slug,
      message: msg,
      ...(attachData && {
        attachment_url: attachData.attachment_url,
        attachment_type: attachData.attachment_type,
        attachment_name: attachData.attachment_name,
      }),
    });
    setInputMsg("");
    clearAttachment();
    setSending(false);
    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMsg, socketStatus, queryId, slug, attachment]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Group messages by date ────────────────────────────────────────────────
  const grouped = messages.reduce((acc, msg) => {
    const dateLabel = formatDate(msg.created_at);
    if (!acc[dateLabel]) acc[dateLabel] = [];
    acc[dateLabel].push(msg);
    return acc;
  }, {});

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 1040,
        }}
      />

      {/* Chat Panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(420px, 100vw)",
          background: "#fff",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.15)",
        }}
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e3a8a, #3b82f6)",
            padding: "14px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
              {queryInfo?.name || "Customer Chat"}
            </div>
            <div style={{ color: "#bfdbfe", fontSize: 12, marginTop: 2 }}>
              {queryInfo?.mobile && `📱 ${queryInfo.mobile}`}
              {queryInfo?.type && ` · ${queryInfo.type}`}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <StatusDot status={socketStatus} />
            <button
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.15)",
                border: "none",
                color: "#fff",
                width: 32,
                height: 32,
                borderRadius: "50%",
                cursor: "pointer",
                fontSize: 18,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.3)")}
              onMouseOut={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.15)")}
            >
              ×
            </button>
          </div>
        </div>

        {/* ── Query Info Bar ──────────────────────────────────────────────── */}
        <div
          style={{
            background: "#eff6ff",
            borderBottom: "1px solid #bfdbfe",
            padding: "8px 16px",
            fontSize: 12,
            color: "#1e40af",
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 600 }}>Query: </span>
            {queryInfo?.short_description}
            {currentQueryStatus === "C" && currentClosingReason && (
              <div style={{ color: "#7c3aed", marginTop: 4, fontWeight: 500 }}>
                Reason: {currentClosingReason}
              </div>
            )}
          </div>
          {currentQueryStatus !== "C" && (
            <button
              onClick={handleEndConversation}
              style={{
                background: "#fef2f2",
                color: "#dc2626",
                border: "1px solid #fca5a5",
                borderRadius: 4,
                padding: "3px 8px",
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "background 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#fee2e2")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#fef2f2")}
            >
              End Chat
            </button>
          )}
        </div>

        {/* ── Messages Area ───────────────────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px 16px",
            background: "#f8fafc",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {messages.length === 0 && socketStatus === "connected" && (
            <div
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#94a3b8",
                gap: 8,
              }}
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              <p style={{ fontSize: 14 }}>No messages yet. Start the conversation!</p>
            </div>
          )}

          {socketStatus === "connecting" && messages.length === 0 && (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8" }}>
              <span>Connecting to chat…</span>
            </div>
          )}

          {Object.entries(grouped).map(([dateLabel, msgs]) => (
            <div key={dateLabel}>
              <DateDivider label={dateLabel} />
              {msgs.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  isAdmin={msg.sender_type === "tenant"}
                />
              ))}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* ── Input Area ─────────────────────────────────────────────────── */}
        <div
          style={{
            borderTop: "1px solid #e2e8f0",
            padding: "12px 16px",
            background: "#fff",
            flexShrink: 0,
          }}
        >
          {currentQueryStatus === "C" ? (
            <div
              style={{
                background: "#f1f5f9",
                border: "1px solid #cbd5e1",
                borderRadius: 12,
                padding: "12px",
                textAlign: "center",
                color: "#64748b",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              🔒 Conversation Ended
              {currentClosingReason && (
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                  {currentClosingReason}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Attachment Preview */}
              {attachment && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#f1f5f9",
                    borderRadius: 10,
                    padding: "8px 12px",
                    marginBottom: 8,
                  }}
                >
                  {attachment.type === "image" ? (
                    <img src={attachment.preview} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover" }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: 8, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="1.5">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {attachment.file.name}
                    </div>
                    <div style={{ fontSize: 10, color: "#94a3b8" }}>{(attachment.file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button
                    onClick={clearAttachment}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 18, padding: 4 }}
                  >
                    ×
                  </button>
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,application/pdf"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-end",
                  background: "#f8fafc",
                  borderRadius: 12,
                  border: "1.5px solid #e2e8f0",
                  padding: "6px 6px 6px 12px",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.currentTarget.style.borderColor = "#3b82f6")}
                onBlur={(e) => (e.currentTarget.style.borderColor = "#e2e8f0")}
              >
                {/* Attachment button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={socketStatus !== "connected" || uploading}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: socketStatus === "connected" && !uploading ? "pointer" : "default",
                    color: "#64748b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    flexShrink: 0,
                    opacity: socketStatus === "connected" && !uploading ? 1 : 0.4,
                    transition: "color 0.2s",
                  }}
                  onMouseOver={(e) => { if (socketStatus === "connected") e.currentTarget.style.color = "#3b82f6"; }}
                  onMouseOut={(e) => (e.currentTarget.style.color = "#64748b")}
                  title="Attach file"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>

                <textarea
                  ref={inputRef}
                  value={inputMsg}
                  onChange={(e) => setInputMsg(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={uploading ? "Uploading file..." : "Type a message… (Enter to send)"}
                  disabled={socketStatus !== "connected" || sending || uploading}
                  rows={1}
                  style={{
                    flex: 1,
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    resize: "none",
                    fontSize: 14,
                    lineHeight: 1.5,
                    maxHeight: 120,
                    overflowY: "auto",
                    fontFamily: "inherit",
                    color: "#1e293b",
                  }}
                  onInput={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
                  }}
                />
                <button
                  onClick={handleSend}
                  disabled={(!inputMsg.trim() && !attachment) || socketStatus !== "connected" || sending || uploading}
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    border: "none",
                    background:
                      (!inputMsg.trim() && !attachment) || socketStatus !== "connected" || uploading
                        ? "#e2e8f0"
                        : "linear-gradient(135deg, #3b82f6, #2563eb)",
                    color: (!inputMsg.trim() && !attachment) || socketStatus !== "connected" || uploading ? "#94a3b8" : "#fff",
                    cursor:
                      (!inputMsg.trim() && !attachment) || socketStatus !== "connected" || uploading ? "default" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "all 0.2s",
                  }}
                >
                  {uploading ? (
                    <span style={{ width: 16, height: 16, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  )}
                </button>
              </div>
              {socketStatus === "disconnected" && (
                <p style={{ fontSize: 11, color: "#ef4444", margin: "6px 0 0", textAlign: "center" }}>
                  ⚠ Connection lost. Messages cannot be sent.
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Pulse + spin animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default SupportChat;
